import { data } from 'react-router-dom'

import { reauthenticate } from '@/lib/reauth'

/**
 * The one failure policy for both API clients (the generated client's
 * middleware and AuthenticatedApiClient for PocketBase).
 *
 * - 401: the session is gone. The transport starts signing in again
 *   (reauthenticate) and throws react-router
 *   `data('unauthorized', { status: 401 })` so the caller stops; screens
 *   never handle it themselves.
 * - 403 with an application error code in the body (`{ code }`, e.g. 7001
 *   new-programmes limit): a refusal of this action, not of the session.
 *   It stays a typed failure (ResponseError / FetchError) so callers can
 *   read the code with apiErrorCode and show the right screen.
 * - 403 without a code: treated as an expired session, like 401.
 * - anything else: left to the client's own error type.
 */
export function unauthorized(url: string): never {
    // The exchange is the sign-in itself: its failure is shown, not retried.
    if (!/\/api\/v1\/auth\/exchange(\?|$)/.test(url)) reauthenticate()
    throw data('unauthorized', { status: 401 })
}

/** Whether a failed response means "sign in again". */
export function isSessionFailure(
    status: number,
    appCode: string | undefined
): boolean {
    return status === 401 || (status === 403 && !appCode)
}

/** The gateway's application error code, from a JSON body. */
export function errorBodyCode(body: unknown): string | undefined {
    const code = (body as { code?: unknown } | null | undefined)?.code
    return typeof code === 'string' && code !== '' ? code : undefined
}

/** Reads a response's error code without consuming its body. */
export async function responseErrorCode(
    response: Response
): Promise<string | undefined> {
    try {
        return errorBodyCode(await response.clone().json())
    } catch {
        return undefined
    }
}
